/** Sticky action bar under the settings ("Xử lý ảnh này" in the desktop app); the token charge is written on the button. */
import { useId } from "react";
import type { Me } from "../api";
import { RENDER } from "../messages";

export type AuthState = "loading" | "guest" | "in";

interface Props {
  auth: AuthState;
  me: Me | null;
  cost: number;
  /** a premium model is chosen: it is paid from the top-up wallet only */
  topupOnly?: boolean;
  submitting: boolean;
  disabled: boolean;
  onRender: () => void;
  /** Why the button cannot send yet (shown quietly) … */
  hint: string | null;
  /** … or why the last press did not send (announced). */
  alert: string | null;
  notice: { text: string; ok: boolean } | null;
}

function subLine(auth: AuthState, me: Me | null, cost: number, topupOnly: boolean): string {
  if (auth === "guest") return RENDER.guest;
  if (auth === "loading" || !me) return RENDER.checking;
  if (me.unmetered) return RENDER.free;
  if (topupOnly) {
    const topup = me.topup ?? 0;
    return topup >= cost ? RENDER.afterTopup(topup - cost) : RENDER.shortTopup(topup);
  }
  return me.total >= cost ? RENDER.after(me.total - cost) : RENDER.short(me.total);
}

export function RenderBar({ auth, me, cost, topupOnly = false, submitting, disabled, onRender, hint, alert,
                            notice }: Props) {
  const hintId = useId();
  return (
    <div className="action-bar">
      <button type="button" className="btn btn-primary btn-render" disabled={disabled || submitting} onClick={onRender}
              aria-describedby={hint && !alert ? hintId : undefined}>
        <span className="btn-main">{submitting ? RENDER.sending : RENDER.label(cost)}</span>
        {!submitting && <span className="btn-sub">{subLine(auth, me, cost, topupOnly)}</span>}
      </button>
      {alert ? <p role="alert" className="error bar-msg">{alert}</p>
        : hint && <p id={hintId} className="hint bar-msg">{hint}</p>}
      {notice && <p role="status" className={`bar-msg ${notice.ok ? "ok" : "error"}`}>{notice.text}</p>}
    </div>
  );
}
